/*
 * @author BSG <dev@bsgroup.eu>
 * @copyright Better Software Group S.A.
 * @version: 1.0
 */
import { useCallback, useEffect, useState } from "react";

import { usePlayerTranslation } from "../../../../../../shared/context/PlayerTranslationContext";
import { MediaType } from "../../../../../../shared/types/enums";
import {
  usePlayerController,
  usePlayerSettings,
} from "../../../../../BasePlayer";
import { usePlayerCurrentMedia } from "../../../../../Controller";
import { useToggleVisibility } from "../../../../Base";

import { useAvailableSection } from "./AvailableSectionContext";

export const usePlaybackRateSettings = () => {
  const {
    playbackRate: { defaultRate, rates },
  } = usePlayerSettings();

  const { media } = usePlayerCurrentMedia();
  const { setAvailableStatus } = useAvailableSection();

  const { player } = usePlayerController();
  const { toggleVisibility } = useToggleVisibility();

  const { t } = usePlayerTranslation();

  const [currentPlaybackRate, setCurrentPlaybackRate] =
    useState<number>(defaultRate);

  useEffect(() => {
    const unsubscribe = player.onEvent("ratechange", () => {
      setCurrentPlaybackRate(player.playbackRate());
    });

    return () => unsubscribe();
  }, [player]);

  useEffect(() => {
    setAvailableStatus(
      "playback-rate",
      media?.MediaTypeCode !== MediaType.Live &&
        media?.MediaTypeCode !== MediaType.Match &&
        media?.MediaTypeCode !== MediaType.Event,
    );
  }, [media?.MediaTypeCode, setAvailableStatus]);

  const onRateSelect = useCallback(
    (value: number) => {
      player.playbackRate(value);
      toggleVisibility();
    },
    [player, toggleVisibility],
  );

  const formatPlaybackRate = (playbackRate: number): string => {
    return playbackRate === defaultRate
      ? t("PLAYER_DEFAULT_PLAYBACK_RATE")
      : `${playbackRate}x`;
  };

  return {
    rates,
    currentPlaybackRate,
    onRateSelect,
    formatPlaybackRate,
  };
};
